import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import PatientService from '../../../services/patientService';
import DentalNoteService from '../../../services/dentalNoteService';
import DiagnosisService from '../../../services/diagnosisService';
import PatientSectionHeader from '../common/PatientSectionHeader';
import Modal from '../../common/Modal';
import Button from '../../common/Button';
import Odontogram from './Odontogram';
import { getSurfaceName } from '../../../utils/dentalSurfaceUtils';
import './DentalChart.css';
import './DiagnosisStyles.css';

const DentalChart = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();

  const [patient, setPatient] = useState(null);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedToothData, setSelectedToothData] = useState(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [mode, setMode] = useState('adult'); // 'adult' | 'pediatric'
  const [expandedNotes, setExpandedNotes] = useState(new Set()); // Para manejar qué notas están expandidas

  // Función para formatear fecha
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 1) {
      return 'Hoy';
    } else if (diffDays === 2) {
      return 'Ayer';
    } else if (diffDays <= 7) {
      return `Hace ${diffDays - 1} días`;
    } else {
      return date.toLocaleDateString('es-ES', { 
        day: 'numeric', 
        month: 'short', 
        year: 'numeric' 
      });
    }
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('es-ES', { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Función para truncar texto inteligentemente (por palabras)
  const truncateText = (text, maxLength = 150) => {
    if (!text || text.length <= maxLength) return text;
    
    const truncated = text.substring(0, maxLength);
    const lastSpaceIndex = truncated.lastIndexOf(' ');
    
    // Si hay un espacio cerca del final, cortamos por palabra completa
    if (lastSpaceIndex > maxLength * 0.8) {
      return truncated.substring(0, lastSpaceIndex) + '...';
    }
    
    // Si no, cortamos por carácter
    return truncated + '...';
  };

  // Cargar datos del paciente
  useEffect(() => {
    const loadPatient = async () => {
      if (patientId && auth.user?.access_token) {
        try {
          const patientData = await PatientService.getPatientById(patientId, auth.user.access_token);
          setPatient(patientData);
        } catch (error) {
          console.error('Error loading patient:', error);
        }
      }
    };
    loadPatient();
  }, [patientId, auth.user?.access_token]);

  // Cargar notas dentales
  const loadDentalNotes = React.useCallback(async () => {
    if (patientId && auth.user?.access_token) {
      try {
        setLoading(true);
        const response = await DentalNoteService.getDentalNotesByPatient(
          patientId, 
          { status: 'active', limit: 100 }, 
          auth.user.access_token
        );
        
        const notesData = response.data || [];
        
        // Recopilar todos los diagnósticos únicos que necesitamos cargar
        const uniqueDiagnosisIds = new Set();
        notesData.forEach(note => {
          if (note.subNotes && note.subNotes.length > 0) {
            note.subNotes.forEach(subNote => {
              if (subNote.diagnosisId) {
                uniqueDiagnosisIds.add(subNote.diagnosisId);
              }
            });
          } else if (note.diagnosisId) {
            uniqueDiagnosisIds.add(note.diagnosisId);
          }
        });

        // Cargar el catálogo de diagnósticos en una sola consulta
        let diagnosisCache = new Map();
        if (uniqueDiagnosisIds.size > 0) {
          diagnosisCache = await DiagnosisService.getDiagnosesMap(auth.user.access_token).catch(error => {
            console.error('Error loading diagnoses:', error);
            return new Map();
          });
        }

        // Procesar las notas usando el cache de diagnósticos
        const notesWithDiagnoses = notesData.map(note => {
          // Para notas multi-diagnóstico
          if (note.subNotes && note.subNotes.length > 0) {
            const subNotesWithDiagnoses = note.subNotes.map(subNote => {
              const diagnosis = diagnosisCache.get(subNote.diagnosisId);
              return {
                ...subNote,
                diagnosisName: diagnosis?.name || 'Diagnóstico no encontrado',
                diagnosisColor: diagnosis?.color || '#gray',
                isCompleteTooth: diagnosis?.isCompleteTooth || false
              };
            });
            
            return { 
              ...note, 
              subNotes: subNotesWithDiagnoses,
              isMultiDiagnosis: true 
            };
          }
          // Para notas tradicionales
          else if (note.diagnosisId) {
            const diagnosis = diagnosisCache.get(note.diagnosisId);
            return { 
              ...note, 
              diagnosisName: diagnosis?.name || 'Diagnóstico no encontrado', 
              diagnosisColor: diagnosis?.color || '#gray', 
              isCompleteTooth: diagnosis?.isCompleteTooth || false,
              isMultiDiagnosis: false 
            };
          }
          return { ...note, isMultiDiagnosis: false };
        });
        
        setNotes(notesWithDiagnoses);
      } catch (error) {
        console.error('Error loading dental notes:', error);
        setNotes([]);
      } finally {
        setLoading(false);
      }
    }
  }, [patientId, auth.user?.access_token]);

  useEffect(() => {
    loadDentalNotes();
  }, [loadDentalNotes]);

  // Función para convertir nombres de superficies de la DB al formato del odontograma
  // Esta es la misma función que se usa en EditDentalNote.js
  const denormalizeSurface = (s) => {
    // Map clinical terms back to UI keys
    const map = {
      'oclusal/incisal': 'top',
      'distal': 'right',
      'lingual/palatino': 'bottom',
      'mesial': 'left',
      'centro': 'center',
    };
    return map[s] || s;
  };



  // Crear un mapa de todas las selecciones para mostrar en el odontograma
  // Usa un color uniforme azul claro para indicar actividad dental (historial)
  const createToothSelectionMap = () => {
    const selectionMap = {};
    const HISTORY_COLOR = 'blue'; // Mismo color que el plan de tratamiento
    
    notes.forEach((note) => {
      // Notas multi-diagnóstico: procesar cada sub-nota
      if (note.isMultiDiagnosis && note.subNotes && Array.isArray(note.subNotes)) {
        note.subNotes.forEach((subNote) => {
          if (subNote.selections && Array.isArray(subNote.selections)) {
            subNote.selections.forEach((selection) => {
              const toothLabel = selection.tooth;
              const surface = denormalizeSurface(selection.surface);
              
              if (!selectionMap[toothLabel]) {
                selectionMap[toothLabel] = {};
              }
              
              // Siempre usar color uniforme para historial
              selectionMap[toothLabel][surface] = HISTORY_COLOR;
            });
          }
        });
      }
      // Formato tradicional: con array de selections
      else if (note.selections && Array.isArray(note.selections)) {
        note.selections.forEach((selection) => {
          const toothLabel = selection.tooth;
          const surface = denormalizeSurface(selection.surface);
          
          if (!selectionMap[toothLabel]) {
            selectionMap[toothLabel] = {};
          }
          
          // Siempre usar color uniforme para historial
          selectionMap[toothLabel][surface] = HISTORY_COLOR;
        });
      }
      // Formato antiguo: propiedades directas
      else if (note.tooth && note.surface) {
        const toothLabel = note.tooth;
        const surface = denormalizeSurface(note.surface);
        
        if (!selectionMap[toothLabel]) {
          selectionMap[toothLabel] = {};
        }
        
        selectionMap[toothLabel][surface] = HISTORY_COLOR;
      }
    });
    
    return selectionMap;
  };

  // Obtener notas para una sección específica de un diente
  const getNotesForToothSection = (tooth, surface) => {
    
    return notes.filter(note => {
      // Notas multi-diagnóstico: buscar en cada sub-nota
      if (note.isMultiDiagnosis && note.subNotes && Array.isArray(note.subNotes)) {
        const hasMatch = note.subNotes.some(subNote => {
          if (subNote.selections && Array.isArray(subNote.selections)) {
            return subNote.selections.some(selection => {
              const selectionTooth = selection.tooth;
              const selectionSurface = denormalizeSurface(selection.surface);
              
              const matches = selectionTooth === tooth && selectionSurface === surface;
              console.log('  Comparando sub-nota selección:', selectionTooth, selection.surface, '→', selectionSurface, 'vs', tooth, surface, '→', matches);
              return matches;
            });
          }
          return false;
        });
        
        console.log('  ¿Nota multi-diagnóstico coincide?', hasMatch, 'para nota:', note.id);
        return hasMatch;
      }
      // Formato tradicional: con array de selections
      else if (note.selections && Array.isArray(note.selections)) {
        const hasMatch = note.selections.some(selection => {
          const selectionTooth = selection.tooth;
          const selectionSurface = denormalizeSurface(selection.surface); // Usar la misma función
          
          const matches = selectionTooth === tooth && selectionSurface === surface;
          console.log('  Comparando selección tradicional:', selectionTooth, selection.surface, '→', selectionSurface, 'vs', tooth, surface, '→', matches);
          return matches;
        });
        
        console.log('  ¿Nota tradicional coincide?', hasMatch, 'para nota:', note.id);
        return hasMatch;
      }
      
      // Formato antiguo: tooth y surface directamente en la nota
      if (note.tooth && note.surface) {
        const noteTooth = note.tooth;
        const noteSurface = denormalizeSurface(note.surface); // Usar la misma función
        
        const hasMatch = noteTooth === tooth && noteSurface === surface;
        console.log('  Comparando formato antiguo:', noteTooth, note.surface, '→', noteSurface, 'vs', tooth, surface, '→', hasMatch);
        return hasMatch;
      }
      
      console.log('  Nota sin formato válido:', note.id);
      return false;
    }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  };

  // Manejar expansión/contracción de notas
  const toggleNoteExpansion = (noteId) => {
    const newExpandedNotes = new Set(expandedNotes);
    if (newExpandedNotes.has(noteId)) {
      newExpandedNotes.delete(noteId);
    } else {
      newExpandedNotes.add(noteId);
    }
    setExpandedNotes(newExpandedNotes);
  };

  // Resetear expansiones cuando se cierra el modal
  const handleCloseModal = () => {
    setShowNotesModal(false);
    setExpandedNotes(new Set());
    setSelectedToothData(null);
  };

  // Manejar clic en una sección del diente
  const handleToothSectionClick = (tooth, surface) => {
    console.log('=== DENTAL CHART DEBUG ===');
    console.log('Clic en diente:', tooth, 'superficie:', surface);
    console.log('Total de notas cargadas:', notes.length);
    
    // Log de todas las notas para debug
    notes.forEach((note, index) => {
      console.log(`Nota ${index + 1}:`, {
        id: note.id,
        selections: note.selections,
        tooth: note.tooth,
        surface: note.surface,
        text: note.text?.substring(0, 50) + '...'
      });
    });
    
    const sectionNotes = getNotesForToothSection(tooth, surface);
    console.log('Notas filtradas para esta sección:', sectionNotes.length);
    console.log('Notas encontradas:', sectionNotes);
    
    setSelectedToothData({
      tooth,
      surface,
      surfaceLabel: getSurfaceName(tooth, surface),
      notes: sectionNotes
    });
    setShowNotesModal(true);
  };

  const toothSelections = createToothSelectionMap();

  return (
    <div className="dental-chart-container">
      <PatientSectionHeader
        patientId={patientId}
        patientName={patient ? `${patient.firstName} ${patient.lastName}` : null}
        sectionTitle="Cartilla Dental"
        subtitle="Las secciones marcadas tienen registros de odontograma con diagnósticos. Haz clic para ver todas las notas de esa sección."
      />

      <div className="chart-controls">
        <div className="chart-info">
        <div className="notes-summary">
          <i className="fas fa-sticky-note"></i>
          <span>{notes.length} registros de odontograma cargados</span>
        </div>
        </div>
        
        <div className="mode-selector">
          <button 
            className={`mode-btn ${mode === 'adult' ? 'active' : ''}`}
            onClick={() => setMode('adult')}
          >
            <i className="fas fa-user"></i>
            Adulto
          </button>
          <button 
            className={`mode-btn ${mode === 'pediatric' ? 'active' : ''}`}
            onClick={() => setMode('pediatric')}
          >
            <i className="fas fa-child"></i>
            Pediátrico
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-state">
          <i className="fas fa-spinner fa-spin"></i>
          <h3>Cargando cartilla dental...</h3>
        </div>
      ) : (
        <div className="odontogram-container">
          <Odontogram
            mode={mode}
            selected={toothSelections}
            onToggle={handleToothSectionClick}
            showCompleteToothX={false}
          />
        </div>
      )}

      {/* Modal para mostrar notas de la sección seleccionada */}
      {showNotesModal && selectedToothData && (
        <Modal
          isOpen={showNotesModal}
          onClose={handleCloseModal}
          title={`Diente ${selectedToothData.tooth} - ${selectedToothData.surfaceLabel}`}
          size="xl"
        >
          <div className="section-notes-content">
            {selectedToothData.notes.length === 0 ? (
              <div className="no-notes-state">
                <i className="fas fa-sticky-note"></i>
                <h3>No hay notas para esta sección</h3>
                <p>No se han registrado anotaciones de odontograma para el diente {selectedToothData.tooth} - {selectedToothData.surfaceLabel}</p>
              </div>
            ) : (
              <>
                <div className="section-notes-header">
                  <h4>
                    <i className="fas fa-tooth"></i>
                    {selectedToothData.notes.length} nota{selectedToothData.notes.length > 1 ? 's' : ''} encontrada{selectedToothData.notes.length > 1 ? 's' : ''}
                  </h4>
                </div>
                
                <div className="section-notes-list">
                  {selectedToothData.notes.map(note => {
                    return (
                      <div key={note.id} className="section-note-card">
                        <div className="note-header">
                          <div className="note-date-info">
                            <span className="note-date">{formatDate(note.createdAt)}</span>
                            <span className="note-time">{formatTime(note.createdAt)}</span>
                          </div>
                        </div>
                        
                        {/* Para notas multi-diagnóstico: mostrar todos los diagnósticos relevantes */}
                        {note.isMultiDiagnosis && note.subNotes ? (
                          <div className="multi-diagnosis-content">
                            {note.subNotes
                              .filter(subNote => 
                                subNote.selections && 
                                subNote.selections.some(sel => 
                                  sel.tooth === selectedToothData.tooth && 
                                  denormalizeSurface(sel.surface) === selectedToothData.surface
                                )
                              )
                              .map((subNote, index) => (
                                <div key={index} className="sub-note-section">
                                  <div className="sub-note-diagnosis">
                                    <i className="fas fa-stethoscope"></i>
                                    <span>{subNote.diagnosisName}</span>
                                    <span 
                                      className="diagnosis-color-indicator" 
                                      style={{ backgroundColor: subNote.diagnosisColor }}
                                      title={`Color: ${subNote.diagnosisColor}`}
                                    ></span>
                                  </div>
                                  {subNote.text && (
                                    <div className="sub-note-text">
                                      <p>{subNote.text}</p>
                                    </div>
                                  )}
                                </div>
                              ))
                            }
                          </div>
                        ) : (
                          /* Para notas tradicionales */
                          <>
                            {note.diagnosisName && (
                              <div className="note-diagnosis">
                                <i className="fas fa-stethoscope"></i>
                                <span>{note.diagnosisName}</span>
                                <span 
                                  className="diagnosis-color-indicator" 
                                  style={{ backgroundColor: note.diagnosisColor }}
                                  title={`Color: ${note.diagnosisColor}`}
                                ></span>
                              </div>
                            )}
                            
                            <div className="note-text-content">
                              {note.text ? (
                                <div>
                                  <p>
                                    {expandedNotes.has(note.id) ? note.text : truncateText(note.text, 150)}
                                  </p>
                                  {note.text.length > 150 && (
                                    <button 
                                      className="expand-text-btn"
                                      onClick={() => toggleNoteExpansion(note.id)}
                                    >
                                      {expandedNotes.has(note.id) ? (
                                        <>
                                          <i className="fas fa-chevron-up"></i>
                                          Ver menos
                                        </>
                                      ) : (
                                        <>
                                          <i className="fas fa-chevron-down"></i>
                                          Ver más
                                        </>
                                      )}
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <p className="no-description">
                                  <i className="fas fa-sticky-note"></i>
                                  Sin descripción adicional
                                </p>
                              )}
                            </div>
                          </>
                        )}
                        
                        <div className="note-meta">
                          <div className="note-meta-left">
                            <span className="note-mode">
                              <i className={`fas ${note.mode === 'adult' ? 'fa-user' : 'fa-child'}`}></i>
                              {note.mode === 'adult' ? 'Adulto' : 'Pediátrico'}
                            </span>
                            
                            {/* Mostrar información adicional según el tipo de nota */}
                            {note.isMultiDiagnosis ? (
                              <span className="note-multi-selection">
                                <i className="fas fa-layer-group"></i>
                                {note.subNotes?.length || 0} diagnóstico{(note.subNotes?.length || 0) > 1 ? 's' : ''} total{(note.subNotes?.length || 0) > 1 ? 'es' : ''}
                              </span>
                            ) : (
                              note.selections && note.selections.length > 1 && (
                                <span className="note-multi-selection">
                                  <i className="fas fa-plus"></i>
                                  {note.selections.length - 1} sección{note.selections.length - 1 > 1 ? 'es' : ''} más
                                </span>
                              )
                            )}
                          </div>
                          
                          <div className="note-meta-right">
                            <Button 
                              variant="outline"
                              size="sm"
                              icon="fas fa-edit"
                              onClick={() => navigate(`/patients/${patientId}/dental-notes/${note.id}/edit`)}
                              title="Editar nota completa"
                            >
                              Editar
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default DentalChart;
